/**
 * The STO account blocks' property filter (DAT-9223): the Cloudscape PropertyFilter query ↔
 * ListAccountPipeline's `propertyFilter` member.
 *
 * WIRE. `propertyFilter` is a JSON string on a GET query parameter, the same one-level
 * `{operation, groups: [{operation, conditions: [{field, operator, values}]}]}` tree
 * ListTaggedPipelines takes (see utils/sfdcPipelinePropertyFilter.ts), over this block's own
 * four fields. Operators are `in` / `notIn` only. Omitted means NO filter — not a default one —
 * so the default below is always sent explicitly.
 *
 * Not built on queryToPropertyFilter: that translator's field allowlist, date operators and
 * single-value BOOL rule are the Tagged page's, and ACCOUNT_HIERARCHY is not one of its fields.
 * This one is the `=` / `!=` subset of it and nothing more.
 *
 * Which conditions decide what (the human's call, implemented server-side): opportunity
 * conditions (OPP_NAME, SFDC_STAGE) only decide which opportunities count under an account;
 * only account conditions (ACCOUNT_NAME, ACCOUNT_HIERARCHY) decide whether a lighthouse account
 * is listed, even under OR. The page therefore never has to special-case the lighthouse block.
 */
import type { PropertyFilterProps } from '@amzn/awsui-components-react';

/** The filter's fields; each is the PropertyFilter `key` AND the wire `field`. */
export const ACCOUNT_PIPELINE_FIELDS = ['SFDC_STAGE', 'ACCOUNT_NAME', 'OPP_NAME', 'ACCOUNT_HIERARCHY'] as const;
export type AccountPipelineField = (typeof ACCOUNT_PIPELINE_FIELDS)[number];
const KNOWN_FIELDS: ReadonlySet<string> = new Set(ACCOUNT_PIPELINE_FIELDS);

/**
 * The block's stage buckets, in the service's order (IVTHub `AccountPipelineStageBuckets`).
 *
 * Not the Tagged page's buckets: there Closed Lost and Completed fall in `others`. Hardcoded
 * rather than read from ListAccountPipelineFilterOptions(SFDC_STAGE) because the set is part of
 * the contract — an unknown token is a 400 — and the default token below has to render with
 * its labels before any request returns. The labels add what each bucket folds in, which the
 * service's bare stage names do not say.
 */
export const STAGE_BUCKETS: readonly { value: string; label: string }[] = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'techValidation', label: 'Technical Validation' },
  { value: 'bizValidation', label: 'Business Validation' },
  { value: 'committed', label: 'Committed' },
  { value: 'launched', label: 'Launched / Completed' },
  { value: 'closedLost', label: 'Closed Lost' },
  { value: 'others', label: 'Others (On-hold, none, other)' },
];
const STAGE_LABEL: ReadonlyMap<string, string> = new Map(STAGE_BUCKETS.map((b) => [b.value, b.label]));

/**
 * PM's default for both blocks: hide Launched (with Completed) and Closed Lost, nothing else —
 * On-hold and a missing stage stay. A factory, so two blocks never share one query object.
 */
export function defaultAccountPipelineQuery(): PropertyFilterProps.Query {
  return {
    operation: 'and',
    tokens: [{ propertyKey: 'SFDC_STAGE', operator: '!=', value: ['launched', 'closedLost'] }],
  };
}

// ── ACCOUNT_HIERARCHY values ────────────────────────────────────────────────

/** Display names of ListHierarchyNodes' levels (HierarchyLevel values). */
const LEVEL_LABEL: Readonly<Record<string, string>> = {
  sh_l3: 'L3',
  sh_l4: 'L4',
  sh_l5: 'L5',
  sh_l6: 'L6',
  sh_l7: 'L7',
  territory: 'Territory',
};

/** `<level>:<name>` — the wire value. The name may itself contain `:`; only the first splits. */
export function hierarchyValue(level: string, name: string): string {
  return `${level}:${name}`;
}

/** `sh_l5:AUTO` → `L5 · AUTO`. An unparseable value is shown as it is. */
export function hierarchyLabel(value: string): string {
  const at = value.indexOf(':');
  if (at <= 0) return value;
  const level = value.slice(0, at);
  return `${LEVEL_LABEL[level] ?? level} · ${value.slice(at + 1)}`;
}

/**
 * Applied tokens render their raw value unless the operator carries `format` (the option
 * labels only style the dropdown — see pipelineFilterShared.formatGreenfield), so the two
 * fields whose values are tokens get one.
 */
const formatValues = (one: (v: string) => string) => (value: unknown) =>
  (Array.isArray(value) ? value : [value]).map((v) => one(String(v))).join(', ');
export const formatStage = formatValues((v) => STAGE_LABEL.get(v) ?? v);
export const formatHierarchy = formatValues(hierarchyLabel);

// ── query → tree ────────────────────────────────────────────────────────────

export interface AccountPipelineCondition {
  field: AccountPipelineField;
  operator: 'in' | 'notIn';
  values: string[];
}
export interface AccountPipelineFilterTree {
  operation: 'and' | 'or';
  groups: { operation: 'and' | 'or'; conditions: AccountPipelineCondition[] }[];
}

type TokenOrGroup = PropertyFilterProps.Token | PropertyFilterProps.TokenGroup;
const isGroup = (n: TokenOrGroup): n is PropertyFilterProps.TokenGroup =>
  Array.isArray((n as PropertyFilterProps.TokenGroup).tokens);

function toCondition(t: PropertyFilterProps.Token): AccountPipelineCondition | null {
  if (!t.propertyKey || !KNOWN_FIELDS.has(t.propertyKey)) return null;
  const operator = t.operator === '=' ? 'in' : t.operator === '!=' ? 'notIn' : null;
  if (!operator) return null;
  const raw = t.value == null ? [] : Array.isArray(t.value) ? t.value : [t.value];
  const values = raw.map((v) => String(v)).filter((v) => v.length > 0);
  // An emptied token is no constraint, not "matches nothing".
  if (values.length === 0) return null;
  return { field: t.propertyKey as AccountPipelineField, operator, values };
}

/**
 * The tree, or `undefined` when nothing usable is left (the member is then omitted; the
 * service rejects an empty string). Reads `tokenGroups` when the control is in grouping mode,
 * else the flat `tokens`; a bare token is a one-condition group. Cloudscape nests one level
 * at most, which is also all the service accepts.
 */
export function queryToAccountPipelineFilter(
  query: PropertyFilterProps.Query,
): AccountPipelineFilterTree | undefined {
  const nodes: readonly TokenOrGroup[] =
    query.tokenGroups && query.tokenGroups.length > 0 ? query.tokenGroups : (query.tokens ?? []);
  const groups: AccountPipelineFilterTree['groups'] = [];
  for (const node of nodes) {
    if (isGroup(node)) {
      const conditions = (node.tokens ?? [])
        .filter((c): c is PropertyFilterProps.Token => !isGroup(c))
        .map(toCondition)
        .filter((c): c is AccountPipelineCondition => c !== null);
      if (conditions.length > 0) groups.push({ operation: node.operation === 'or' ? 'or' : 'and', conditions });
    } else {
      const c = toCondition(node);
      if (c) groups.push({ operation: 'and', conditions: [c] });
    }
  }
  if (groups.length === 0) return undefined;
  return { operation: query.operation === 'or' ? 'or' : 'and', groups };
}

export function queryToAccountPipelineFilterParam(query: PropertyFilterProps.Query): string | undefined {
  const tree = queryToAccountPipelineFilter(query);
  return tree ? JSON.stringify(tree) : undefined;
}

// ── the request-size cap ────────────────────────────────────────────────────

/**
 * The bytes a value takes in the query string, exactly as the generated client writes it
 * (`@smithy/util-uri-escape`: encodeURIComponent plus `!'()*`). A CJK character is 9 bytes.
 *
 * Only ever given `JSON.stringify` output, which writes a lone surrogate (a half-pasted emoji) as
 * the ASCII escape `\udXXX`, so encodeURIComponent cannot throw on it here — nor in the client,
 * which encodes the same string.
 */
export function encodedLength(value: string): number {
  return encodeURIComponent(value).replace(/[!'()*]/g, '%XX').length;
}

/**
 * Ceiling on the ENCODED `propertyFilter` value, in bytes.
 *
 * The API's WAF (AWSManagedRulesCommonRuleSet, SizeRestrictions_QUERYSTRING) refuses a query
 * string over 2048 bytes with a 403, before the service sees it. The human's call (DAT-9223):
 * the frontend caps the filter; no WAF, CDK or API change. Measured on the URL this client
 * builds (accountPipelineFilter.test.ts pins it through the real serializer): everything but
 * the filter — window, block, industry, score floor and a real 60-character paging token — is
 * 166 bytes, plus 16 for `&propertyFilter=`. Next to the default stage condition the WAF trips
 * at the 19th eight-character Chinese name; this cap stops at 16 (a 1871-byte query string),
 * which leaves about 175 bytes for a longer token or industry code. That margin assumes industry
 * codes stay short (they are 2-10 characters today; the service would accept up to 256, and one
 * over ~150 would need this number lowered).
 */
export const MAX_FILTER_ENCODED_BYTES = 1700;

/** The service's own structural caps (PropertyFilterEngine), so a tree it would 400 is refused here. */
export const MAX_GROUPS = 10;
export const MAX_CONDITIONS_PER_GROUP = 10;
export const MAX_VALUES_PER_CONDITION = 200;
/** PropertyFilterEngine.MAX_VALUE_LEN: a longer value is a 400. */
export const MAX_VALUE_CHARS = 512;

export type FilterRejection =
  | 'invalid-value'
  | 'too-long'
  | 'too-many-groups'
  | 'too-many-conditions'
  | 'too-many-values';

/** Every value of a filter token, as `<propertyKey>\u0000<value>`. */
function tokenValueKeys(query: PropertyFilterProps.Query): string[] {
  const nodes: readonly TokenOrGroup[] =
    query.tokenGroups && query.tokenGroups.length > 0 ? query.tokenGroups : (query.tokens ?? []);
  const leaves = nodes.flatMap((n) => (isGroup(n) ? (n.tokens ?? []).filter((c) => !isGroup(c)) : [n])) as PropertyFilterProps.Token[];
  return leaves.flatMap((t) => {
    const raw = t.value == null ? [] : Array.isArray(t.value) ? t.value : [t.value];
    return raw.map((v) => `${t.propertyKey ?? ''}\u0000${String(v)}`);
  });
}

const STAGE_VALUES: ReadonlySet<string> = new Set(STAGE_BUCKETS.map((b) => b.value));
/** `<level>:<name>`, level a HierarchyLevel wire value and a non-blank name (the service refuses a
 *  blank one, and a trailing space or an IME's full-width space is easy to type). */
const HIERARCHY_VALUE = new RegExp(`^(${Object.keys(LEVEL_LABEL).join('|')}):[\\s\\S]*\\S`, 'u');

/**
 * The query's values the service would refuse with a 400. Cloudscape turns text typed after
 * `Property =` into a token on Enter even with free-text filtering off, so a value can arrive
 * that no list offered: `Launched` for the bucket `launched`, a hierarchy name without its level.
 *
 * Judged by shape, not by "is it among the loaded options": an exact name pasted and entered
 * before its option has loaded is a valid request, and refusing it would also lose the text
 * (Cloudscape clears the input either way). A name the service does not have matches nothing,
 * which the empty state then says.
 */
export function invalidValues(query: PropertyFilterProps.Query): string[] {
  return tokenValueKeys(query)
    .map((k) => [k.slice(0, k.indexOf('\u0000')), k.slice(k.indexOf('\u0000') + 1)] as const)
    .filter(([field, value]) =>
      field === 'SFDC_STAGE'
        ? !STAGE_VALUES.has(value)
        : field === 'ACCOUNT_HIERARCHY'
          ? !HIERARCHY_VALUE.test(value) || value.length > MAX_VALUE_CHARS
          : value.length > MAX_VALUE_CHARS,
    )
    .map(([, value]) => value);
}

/**
 * Options for the values already in the query, so that each stays listed — and checked — in
 * its token's editor however the candidates have changed since it was picked (a new window or
 * industry reloads them). Cloudscape's editor keeps only the values it finds among the options:
 * one missing would be dropped the next time the token is edited.
 */
export function optionsForQueryValues(
  query: PropertyFilterProps.Query,
  existing: readonly PropertyFilterProps.FilteringOption[],
): PropertyFilterProps.FilteringOption[] {
  const have = new Set(existing.map((o) => `${o.propertyKey ?? ''}\u0000${o.value ?? ''}`));
  const missing = [...new Set(tokenValueKeys(query))].filter((k) => !have.has(k));
  return missing.map((k) => {
    const at = k.indexOf('\u0000');
    const propertyKey = k.slice(0, at);
    const value = k.slice(at + 1);
    const label =
      propertyKey === 'SFDC_STAGE' ? formatStage(value) : propertyKey === 'ACCOUNT_HIERARCHY' ? hierarchyLabel(value) : value;
    return { propertyKey, value, label };
  });
}

/** Why the service (or its WAF) would refuse this query, or `null` when it is fine to send. */
export function rejectionOf(query: PropertyFilterProps.Query): FilterRejection | null {
  if (invalidValues(query).length > 0) return 'invalid-value';
  const tree = queryToAccountPipelineFilter(query);
  if (!tree) return null;
  if (tree.groups.length > MAX_GROUPS) return 'too-many-groups';
  if (tree.groups.some((g) => g.conditions.length > MAX_CONDITIONS_PER_GROUP)) return 'too-many-conditions';
  if (tree.groups.some((g) => g.conditions.some((c) => c.values.length > MAX_VALUES_PER_CONDITION))) {
    return 'too-many-values';
  }
  if (encodedLength(JSON.stringify(tree)) > MAX_FILTER_ENCODED_BYTES) return 'too-long';
  return null;
}

/**
 * What the block says when a change is refused. The change is not applied: the filter keeps
 * the last state that could be sent, so the list on screen still matches the tokens.
 */
export const REJECTION_TEXT: Record<FilterRejection, string> = {
  'invalid-value': '筛选值无效：Pipeline Status 和 Account Hierarchy 请从下拉列表中选择，名称不能超过 512 个字符。本次修改未生效。',
  'too-long': '筛选条件已达上限：所选的值太多或太长，超出了请求长度上限，本次修改未生效。请减少所选的值后再试。',
  'too-many-groups': `筛选条件已达上限：最多 ${MAX_GROUPS} 组条件，本次修改未生效。`,
  'too-many-conditions': `筛选条件已达上限：每组最多 ${MAX_CONDITIONS_PER_GROUP} 个条件，本次修改未生效。`,
  'too-many-values': `筛选条件已达上限：每个条件最多选 ${MAX_VALUES_PER_CONDITION} 个值，本次修改未生效。`,
};
